import React, { useEffect, useState } from "react";
import { fetchUserInfo, getAppUrl, authenticatePredict } from "../../requestors/loginRequestor";
import type { AppKey } from "../../requestors/loginRequestor";
import { relayTokens } from "../../features/auth/tokenRelay";
import { signOutRedirect } from "../../helpers/signOutRedirect";
import { Sun, Moon, Search, User } from "lucide-react";
import { useTheme } from "../../components/ThemeProvider";
import { Input, Button } from "@mitkatadvisory/datasurfr-ui-library";
import dataSurfrDarkLogo from "../../assets/svg/datasurfrDarkLogo.svg";
import dataSurfrWhiteLogo from "../../assets/svg/datasurfrWhiteLogo.svg";
import googlePlay from "../../assets/svg/googlePlay.svg";
import appleLight from "../../assets/svg/appleLight.svg";
import appleDark from "../../assets/svg/appleDark.svg";
import SamAiLogo from "../../assets/svg/samAiLogo.svg";
import GSOCvectorDark from "../../assets/svg/GVectorDark.svg";
import goSafeLogo from "../../assets/svg/goSafeLogo.svg";
import travelvectorDark from "../../assets/svg/tVectorDark.svg";
import AdminLogo from "../../assets/svg/adminLogo.svg";
import NexAlertLogo from "../../assets/svg/NexAlertLogo.svg";
import cybervectorDark from "../../assets/svg/cybVectorDark.svg";
import commonvectorDark from "../../assets/svg/comVectorDark.svg";
import GSOCvectorLight from "../../assets/svg/GVectorLight.svg";
import travelvectorLight from "../../assets/svg/tVectorLight.svg";
import cybervectorLight from "../../assets/svg/cybVectorLight.svg";
import commonvectorLight from "../../assets/svg/comVectorLight.svg";
import predict_Logo from "../../assets/svg/predictPlainLogo.svg";
import loginScreenDark from "../../assets/login.png";
import loginScreenLight from "../../assets/loginLight.png";
import Loader from "../../components/loader/Loader";

interface HomeProps {
  accessInfo: {
    access_token: string;
    refresh_token: string;
  };
}

function isDarkTheme(theme: "dark" | "light" | "system") {
  if (theme === "dark") return true;
  if (theme === "system") {
    return document.documentElement.classList.contains("dark");
  }
  return false;
}

const Home: React.FC<HomeProps> = ({ accessInfo }) => {
  const [allowedApps, setAllowedApps] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const { theme, setTheme } = useTheme();

  const isDark = isDarkTheme(theme);
  const toggleTheme = () => setTheme(isDark ? "light" : "dark");

  useEffect(() => {
    const getInfo = async () => {
      if (import.meta.env.DEV && import.meta.env.VITE_BYPASS_AUTH === "true") {
        setAllowedApps(["gosafe", "samai", "predict", "nexalert", "internal"]);
        setUserName("Dev User");
        setLoading(false);
        return;
      }

      try {
        await relayTokens(accessInfo?.access_token, accessInfo?.refresh_token);
        const data = await fetchUserInfo(accessInfo?.access_token);

        // New structure
        const role = (data?.userAttributes?.["custom:role"] || "").toUpperCase();
        const clientSettings = data?.clientSettings || {};
        if (data?.userAttributes?.name) setUserName(data.userAttributes.name);
        if (data?.userAttributes?.email) setUserEmail(data.userAttributes.email);

        // Redirect for ADMIN/PARTNER
        if (role === "ADMIN" || role === "PARTNER") {
          window.location.href =
            import.meta.env.VITE_ENVIRONMENT === "development"
              ? import.meta.env.VITE_APP_URL_INTERNAL
              : import.meta.env.VITE_APP_URL_INTERNAL;
          return;
        }

        // For CLIENT_ADMIN / CLIENT_USER
        let allowed: string[] = [];
        if (clientSettings.travelAccessEnabled) allowed.push("gosafe");
        if (clientSettings.samAiAccessEnabled) allowed.push("samai");
        if (clientSettings.predictAccessEnabled) allowed.push("predict");
        if (clientSettings.nexAlertEnabled) allowed.push("nexalert");
        if (role === "CLIENT_ADMIN") allowed.push("internal");

        setAllowedApps(allowed);
      } catch (err) {
        console.error("User info fetch error", err);
      } finally {
        setLoading(false);
      }
    };

    getInfo();
  }, [accessInfo?.access_token, accessInfo?.refresh_token]);

  const apps = [
    {
      name: "Datasurfr-Pro",
      key: "gosafe",
      defination:
        "Unified Operational Risk Intelligence & Response Platform",
      logo: goSafeLogo,
      background: isDark ? travelvectorDark : travelvectorLight,
    },
    {
      name: "Sam AI",
      key: "samai",
      defination:
        "AI-powered risk analyst that generates alerts, advisories, assessments, and reports — enabling faster, smarter intelligence workflows.",
      logo: SamAiLogo,
      background: isDark ? commonvectorDark : commonvectorLight,
    },
    {
      name: "Datasurfr",
      key: "predict",
      defination:
        "AI-driven, analyst-assisted risk intelligence platform that safeguards people, assets, and operations with real-time insights on global threats.",
      logo: predict_Logo,
      background: isDark ? GSOCvectorDark : GSOCvectorLight,
    },
     {
      name: "NexAlert",
      key: "nexalert",
      defination:
        "Real-time alerts that help you make faster, smarter, and safer decisions anytime, anywhere.",
      logo: NexAlertLogo,
      background: isDark ? commonvectorDark : commonvectorLight,
    },
     {
      name: "Admin",
      key: "internal",
      defination:
        "Central console for managing users, roles, and configurations across the Datasurfr platform.",
      logo: AdminLogo,
      background: isDark ? cybervectorDark : cybervectorLight,
    },
  ];

  const filteredApps = apps.filter(
    (app) =>
      app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.key.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return loading ? (
    <Loader />
  ) : (
    <div
      className="text-foreground py-[4rem] px-[3.5rem] w-[100vw] h-[100vh] overflow-scroll"
      style={
        isDark
          ? {
              backgroundImage: `url(${loginScreenDark})`,
              backgroundSize: "cover",
              backgroundPosition: "center center",
              backgroundRepeat: "no-repeat",
              transformOrigin: "center",
            }
          : {
              backgroundImage: `url(${loginScreenLight})`,
              backgroundSize: "cover",
              backgroundPosition: "center center",
              backgroundRepeat: "no-repeat",
              transformOrigin: "center",
            }
      }
    >
      <div className="top--header w-full flex items-center">
        <div className="left--header w-1/3">
          {isDark ? (
            <img
              src={dataSurfrWhiteLogo}
              alt="DataSurfr Logo"
              className="h-7"
            />
          ) : (
            <img src={dataSurfrDarkLogo} alt="DataSurfr Logo" className="h-7" />
          )}
        </div>
        <div className="right--header w-2/3 flex gap-2 items-center justify-end">
          <div className="searchbar">
            <div className="relative flex-1 max-w-3xs ">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground " />
              <Input
                type="search"
                placeholder="Search apps..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 sm:w-[300px] md:w-[380px] h-10 border focus:!border-[#00C2C9] focus:!ring-0 "
                style={{ borderColor: isDark ? "#7e7e7e" : "#00000066" }}
              />
            </div>
          </div>
          {(userName || userEmail) && (
            <div className="flex items-center gap-2">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0"
                style={{
                  backgroundColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)",
                  color: isDark ? "#d1d5db" : "#374151",
                }}
              >
                {userName
                  ? userName
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .toUpperCase()
                      .slice(0, 2)
                  : <User size={16} />}
              </div>
            </div>
          )}
          <Button
            variant="outline"
            onClick={signOutRedirect}
            className="bg-transparent flex items-center justify-center gap-3 h-10 px-6 border text-foreground-600 hover:!bg-[#00C2C9] hover:!border-[#00C2C9] hover:!text-[#030712]"
            style={{ borderColor: isDark ? "#7e7e7e" : "#00000066" }}
          >
            Sign out
          </Button>
          <Button
            variant="outline"
            className={`bg-transparent group flex items-center justify-center
             px-3 py-2 rounded-lg transition-all duration-200
             hover:bg-sidebar-accent/50 text-sidebar-foreground
              hover:text-sidebar-accent-foreground hover:shadow-md
              hover:scale-[1.01] border hover:border-sidebar-accent/20 h-10 ${
                isDark ? "hover:!bg-[#FFFFFF0B]" : "hover:!bg-[#FFFFFF80]"
              } `}
            onClick={toggleTheme}
            style={{ borderColor: isDark ? "#7e7e7e" : "#00000066" }}
          >
            <div className=" flex items-center justify-center  transition-all duration-200">
              {isDark ? (
                <Sun
                  size={18}
                  className="group-hover:rotate-90 transition-transform duration-300"
                />
              ) : (
                <Moon
                  size={18}
                  className="group-hover:-rotate-12 transition-transform duration-300"
                />
              )}
            </div>
          </Button>
        </div>
      </div>
      <div className="middle--body flex align-middle mt-[4.6rem] justify-between">
        <div className="left--body text-4xl font-medium w-2xl leading-[48px]">
          {userName && (
            <p className="text-[26px]">
              Hi <span className="text-[#2c7695]">{userName.split(" ")[0]},</span> Welcome to
            </p>
          )}
          <p>Unified operational risk intelligence and response platform</p>
        </div>
        <div className="right--body w-120 ml-[6.8rem]">
          <p className="text-base font-normal">
            Datasurfr transforms global risk signals into actionable intelligence. Enabling organizations to continuously monitor global operational risks, assess exposure, generate intelligence-backed decisions, and execute coordinated response.</p>
          <div className="store flex align-middle gap-[1.5rem] mt-[2rem]">
            <a
              href="https://apps.apple.com/in/app/datasurfr/id6740094424"
              target="_blank"
              rel="noopener noreferrer"
              className={`store border rounded-[10px] w-[150px] px-[10px] py-[5px] flex align-middle gap-2 cursor-pointer hover:scale-102 transition-transform duration-200 ${
                isDark ? "hover:!bg-[#FFFFFF0B]" : "hover:!bg-[#FFFFFF80]"
              } hover:!border-[#00C2C9]`}
              style={{ borderColor: isDark ? "#7e7e7e" : "#00000066" }}
            >
              {isDark ? (
                <img src={appleLight} alt="available on app store" />
              ) : (
                <img src={appleDark} alt="available on app store" />
              )}
              <div className="store--text">
                <p className="text-muted-foreground text-[0.625rem] font-normal">
                  available on
                </p>
                <p className="text-foreground text-xs font-medium">App store</p>
              </div>
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.datasurfrmobileappclient"
              target="_blank"
              rel="noopener noreferrer"
              className={`store border rounded-[10px] w-[150px] px-[10px] py-[5px] flex align-middle gap-2 cursor-pointer hover:scale-102 transition-transform duration-200 ${
                isDark ? "hover:!bg-[#FFFFFF0B]" : "hover:!bg-[#FFFFFF80]"
              } hover:!border-[#00C2C9]`}
              style={{ borderColor: isDark ? "#7e7e7e" : "#00000066" }}
            >
              <img src={googlePlay} alt="available on Google play" />
              <div className="store--text">
                <p className="text-muted-foreground text-[0.625rem] font-normal">
                  available on
                </p>
                <p className="text-foreground text-xs font-medium">
                  Google play
                </p>
              </div>
            </a>
          </div>
        </div>
      </div>
      <div className="middle--body flex align-middle mt-[8.75rem] gap-7 flex-wrap">
        {filteredApps.map((app) => {
          const isAllowed = allowedApps.includes(app.key);
          if (!isAllowed) return null;
          return (
            <div
              key={app.key}
              className={`app--container app--${app.key} allowed relative rounded-[10px] shadow-[0_17px_30px_0_rgba(0,0,0,0.15)] w-[241px] h-[260px] overflow-hidden cursor-pointer hover:scale-105 transition-transform duration-200`}
              style={{ backgroundColor: "var(--app-background)" }}
              onClick={async () => {
                if (app.key === "predict") {
                  try {
                    const url = await authenticatePredict(userEmail);
                    window.open(url, "_blank", "noopener,noreferrer");
                  } catch (err) {
                    console.error("Predict auth failed", err);
                  }
                  return;
                }
                const url = getAppUrl(app.key as AppKey);
                if (url) window.open(url, "_blank", "noopener,noreferrer");
              }}
            >
              <div
                className="absolute inset-0 w-[257.57px] h-[204.936px] rotate-[5.68deg]"
                style={{
                  backgroundImage: `url(${app.background})`,
                  backgroundSize: "cover",
                  backgroundPosition: "bottom center",
                  backgroundRepeat: "no-repeat",
                  transformOrigin: "center",
                  top: "67px",
                }}
              ></div>
              <div className="relative z-10 px-4 py-5">
                <div className="img--outer border border-[#d2cfcf] bg-[#ffffff] rounded-[50%] w-[55px] h-[55px] flex items-center justify-center overflow-hidden">
                  <img
                    src={app.logo}
                    alt={`${app.name} Logo`}
                    className="w-[30px] h-[30px] object-contain"
                  />
                </div>
                <p className="app--name text-2xl font-bold mt-6">{app.name}</p>
                <p className="app--defination text-xs font-medium mt-2">
                  {app.defination}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Home;
